import { IconButton, LoadingDarkIcon, RefreshIcon, WhiteboardIcon } from "@minervaproject/fds";
import { debounce } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { PageCallback } from "react-pdf/src/shared/types";

import { OpenInNewTab } from "./controls/open_in_new_tab";
import { Pages } from "./controls/pages";
import { ResourceSettingsMenu } from "./controls/resource_settings_menu";
import { ResourceTitle } from "./controls/resource_title";
import { DEFAULT_MAX_ZOOM, DEFAULT_MIN_ZOOM, Zoom } from "./controls/zoom";
import { ExternalResourceWarning } from "./external_resource_warning";
import { TldrawWhiteboard } from "./tldraw_whiteboard";

import { CenteredErrorBoundary, TooltipErrorBoundary } from "components/error_boundary";
import { ForumAPIContext } from "contexts/forum_api_context";
import { useCapabilities } from "hooks/capabilities";
import { useAppSelector } from "hooks/redux_hooks";
import { useIsMobile } from "hooks/use_is_mobile";
import { useUserProperties } from "hooks/use_user_properties";
import { ResourceType } from "shared/types";
import { ZoomPreferenceType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { needsSignatureToAccess } from "utils/resource";
import { getVisiblePanes } from "utils/stage";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "./pdf_resource.scss";

const DEFAULT_ZOOM = 1;
const REACT_PDF_PAGE_SELECTOR = ".react-pdf__Page";

function PdfResource({ resource }: { resource: ResourceType }) {
  const forumAPI = useContext(ForumAPIContext);
  const { canControlClassroom, isVisibleInClass } = useCapabilities();
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const { zoom_preference } = useUserProperties(currentUserId, ["zoom_preference"]) || {};
  const studentsCanDraw = useAppSelector(classroomSelectors.selectStudentsCanDraw());
  const needsSignedUrl = needsSignatureToAccess(resource.type, resource.url);
  const { panes } = useAppSelector(classroomSelectors.selectStage());
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const activeBreakout = useAppSelector(classroomSelectors.selectActiveBreakoutSessionId());
  const isMobile = useIsMobile();
  const isPresentationView = useAppSelector(classroomSelectors.selectIsPresentationView());
  const visiblePanes = getVisiblePanes(
    panes,
    isInPerson,
    isPresentationView,
    isMobile,
    canControlClassroom,
    !!activeBreakout,
  );
  const [resourceUrl, setResourceUrl] = useState(needsSignedUrl ? "" : resource.url);
  const [currentPage, setCurrentPage] = useState(resource.page); // 1-indexed
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(0);
  const [pageHeight, setPageHeight] = useState(0);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [isFirstPageLoaded, setIsFirstPageLoaded] = useState(false);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [offsetTop, setOffsetTop] = useState(0);
  const [showWhiteboardControls, setShowWhiteboardControls] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const whiteboardRef = useRef<HTMLDivElement>(null);

  const getResourceUrl = useCallback(async () => {
    if (needsSignedUrl) {
      const url = await forumAPI.getSignedUrl(resource.id, { contentDisposition: "inline" });
      setResourceUrl(url);
    }
  }, [forumAPI, needsSignedUrl, resource.id]);

  useEffect(() => {
    getResourceUrl();
  }, [getResourceUrl]);

  useEffect(() => {
    window.flightLogger.log(`[PdfResource] change page ${resource.page}`);
    setCurrentPage(resource.page);
  }, [resource.page]);

  // Enable scrolling on the PDF viewer by temporarily disabling pointer events on the whiteboard
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const enablePdfScrolling = () => {
      if (whiteboardRef.current) {
        whiteboardRef.current.style.pointerEvents = "none";
      }

      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (whiteboardRef.current) {
          whiteboardRef.current.style.pointerEvents = "auto";
        }
      }, 500);
    };
    window.addEventListener("wheel", enablePdfScrolling);

    return () => {
      window.removeEventListener("wheel", enablePdfScrolling);
    };
  }, []);

  const adjustZoom = (scale: number) => {
    // Make sure zoom is between the default min/max values of 0.1 and 2.9
    return Math.min(Math.max(scale, DEFAULT_MIN_ZOOM), DEFAULT_MAX_ZOOM);
  };

  const zoomToFit = useCallback(() => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const zoomToFitWidth = Math.floor((containerWidth / pageWidth) * 10) / 10;
    const zoomToFitHeight = Math.floor((containerHeight / pageHeight) * 10) / 10;
    const _zoomToFit = Math.min(zoomToFitWidth, zoomToFitHeight);
    setZoom(adjustZoom(_zoomToFit));
  }, [pageHeight, pageWidth]);

  const zoomToFill = useCallback(() => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    const _zoomToFill = Math.floor((containerWidth / pageWidth) * 10) / 10;
    setZoom(adjustZoom(_zoomToFill));
  }, [pageWidth]);

  // visiblePanes is in the dependency array so that the zoom functions are called whenever
  // the number of panes on stage changes, so that the PDF adjusts to the size of the new pane
  useEffect(() => {
    if (!isFirstPageLoaded) return;
    if (zoom_preference === ZoomPreferenceType.ZoomToFit) {
      zoomToFit();
    } else if (zoom_preference === ZoomPreferenceType.ZoomToFill) {
      zoomToFill();
    }
  }, [zoom_preference, isFirstPageLoaded, visiblePanes.length, zoomToFill, zoomToFit]);

  const _updateOffsets = () => {
    const _pageElement = containerRef.current?.querySelector(
      REACT_PDF_PAGE_SELECTOR,
    ) as HTMLElement;
    if (_pageElement) {
      window.flightLogger.log(
        `[PdfResource] update offsets offsetLeft=${_pageElement.offsetLeft} offsetTop=${_pageElement.offsetTop} scrollLeft=${scrollContainerRef.current?.scrollLeft} scrollTop=${scrollContainerRef.current?.scrollTop}`,
      );
      const scrollContainerElement = scrollContainerRef.current;
      setOffsetLeft(_pageElement.offsetLeft - (scrollContainerElement?.scrollLeft || 0));
      setOffsetTop(_pageElement.offsetTop - (scrollContainerElement?.scrollTop || 0));
    }
  };

  const updateOffsets = useMemo(() => debounce(_updateOffsets, 3), []);

  // Set up listeners and observers
  useEffect(() => {
    // Observe the page wrapper element to update the offsets when the window is resized
    const pageWrapperObserver = new ResizeObserver((entries) => {
      window.flightLogger.log(
        `[PdfResource] pageWrapperObserver triggered contentRect=${JSON.stringify(
          entries[0].contentRect,
        )}`,
      );
      if (entries[0].contentRect.width === 0 || entries[0].contentRect.height === 0) {
        window.flightLogger.log(
          "[PdfResource] pageWrapperObserver triggered with 0 width or height, skipping update offsets",
        );
        return;
      }
      updateOffsets();
    });

    let pageWrapperElement: Element | null | undefined;
    let scrollContainerElement: Element | null | undefined;

    const onScroll = () => {
      updateOffsets();
    };

    const getElements = () => {
      pageWrapperElement = containerRef.current;
      scrollContainerElement = scrollContainerRef.current;
      if (pageWrapperElement && scrollContainerElement) {
        pageWrapperObserver.observe(pageWrapperElement);
        scrollContainerElement.addEventListener("scroll", onScroll);
      } else {
        setTimeout(getElements, 100);
      }
    };
    getElements();

    return () => {
      if (pageWrapperElement) {
        pageWrapperObserver.unobserve(pageWrapperElement);
      }
      if (scrollContainerElement) {
        scrollContainerElement.removeEventListener("scroll", onScroll);
      }
    };
  }, [updateOffsets]);

  const handleDocumentLoad = ({ numPages }: { numPages: number }) => {
    setNumberOfPages(numPages);
  };

  const handlePageLoad = (page: PageCallback) => {
    if (isFirstPageLoaded) return;

    setPageWidth(page.width);
    setPageHeight(page.height);
    setIsFirstPageLoaded(true);
  };

  const handlePageRender = () => {
    window.flightLogger.log("[PdfResource] page rendered");
    _updateOffsets();
  };

  const handleReload = () => {
    if (!resourceUrl) {
      getResourceUrl();
    }
  };
  return (
    <div className="flex flex-column w-full h-full">
      <div className="flex items-center p1 bg-black body-s text-black-tint-70 resource-controls">
        <ResourceTitle resource={resource} />
        {resourceUrl && (
          <>
            <TooltipErrorBoundary text="The zoom buttons cannot be displayed">
              <Zoom zoom={zoom} setZoom={setZoom} />
            </TooltipErrorBoundary>
            {numberOfPages > 1 && (
              <TooltipErrorBoundary text="The navigation buttons cannot be displayed">
                <Pages
                  resource={resource}
                  numberOfPages={numberOfPages}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
              </TooltipErrorBoundary>
            )}
            {(canControlClassroom || studentsCanDraw) && isVisibleInClass && (
              <TooltipErrorBoundary text="The whiteboard controls cannot be displayed">
                <IconButton onClick={() => setShowWhiteboardControls(!showWhiteboardControls)}>
                  <WhiteboardIcon width={16} height={16} color="var(--black-tint-70)" />
                </IconButton>
              </TooltipErrorBoundary>
            )}
            <TooltipErrorBoundary text="The resource settings menu cannot be displayed">
              <ResourceSettingsMenu editVerb="Draw" showZoomSettings />
            </TooltipErrorBoundary>
            <TooltipErrorBoundary text="The refresh button cannot be displayed">
              <IconButton onClick={handleReload}>
                <RefreshIcon width={16} height={16} color="var(--black-tint-70)" />
              </IconButton>
            </TooltipErrorBoundary>
            <TooltipErrorBoundary text="The open-in-a-new-tab button cannot be displayed">
              <OpenInNewTab url={resourceUrl} />
            </TooltipErrorBoundary>
          </>
        )}
      </div>

      <ExternalResourceWarning />

      {resourceUrl ? (
        <CenteredErrorBoundary text="The resource cannot be displayed">
          <div className="flex flex-auto relative" ref={containerRef}>
            <div
              className="flex justify-center w-full"
              ref={scrollContainerRef}
              style={{ overflow: "auto" }}
            >
              <div style={{ maxWidth: "100%" }}>
                <Document
                  file={resourceUrl}
                  onLoadSuccess={handleDocumentLoad}
                  loading={<LoadingDarkIcon className="absolute-center" />}
                  externalLinkTarget="_blank"
                >
                  <Page
                    pageNumber={currentPage}
                    scale={zoom}
                    loading=""
                    onLoadSuccess={handlePageLoad}
                    onRenderSuccess={handlePageRender}
                  />
                </Document>
              </div>
            </div>
            {numberOfPages > 0 && (
              <div
                className="absolute top-0 left-0"
                style={{ right: "20px", bottom: "20px" }} // HACK: 20px is approximately the width of a scrollbar
                ref={whiteboardRef}
              >
                <TldrawWhiteboard
                  resource={resource}
                  showControls={showWhiteboardControls}
                  isOverlay
                  currentPage={currentPage}
                  numberOfPages={numberOfPages}
                  offsetLeft={offsetLeft}
                  offsetTop={offsetTop}
                  zoom={zoom}
                />
              </div>
            )}
          </div>
        </CenteredErrorBoundary>
      ) : (
        <LoadingDarkIcon className="absolute-center" data-testid="loading" />
      )}
    </div>
  );
}

export { PdfResource };
